import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@contexts/Toast';
import { errorMessage, responseTexts } from '@helpers/Constants';
import {
  AddRoleModalProps,
  ModuleAction,
  ModuleActionTag,
  Modules,
  Role,
} from '@helpers/Models';
import { Axios } from '@helpers/ApiService';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import {
  getAvailableTagsFromList,
  contentConversion,
  getColors,
  getFilteredActions,
  insertTag,
} from '@services/OperationService';

export const AddOperationModal: React.FC<AddRoleModalProps> = ({
  isOpen,
  onClose,
  modules,
}) => {
  const { showToastMessage } = useToast();
  const [roles, setRoles] = useState([]);
  const [rolePermissions, setRolePermissions] = useState<number[]>([]);
  const [operationMasters, setOperationMasters] = useState<Modules[]>([]);
  const [selectedModule, setSelectedModule] = useState<Modules | null>(null);
  const [selectedAction, setSelectedAction] = useState<ModuleAction | null>(
    null,
  );
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const editorRef = useRef<HTMLDivElement>(null);
  const [refresh, setRefresh] = useState(false);

  /**
   * useEffect hook that runs when the `isOpen` state changes.
   *
   * When `isOpen` is `true`, it triggers two API calls:
   * 1. `fetchOperationsMasters()` - Fetches master data for operations.
   * 2. `fetchRoles()` - Fetches the list of available roles.
   *
   * This ensures that the necessary data is loaded whenever the UI section (e.g., modal or drawer) is opened.
   *
   * Dependencies: [isOpen]
   */
  useEffect(() => {
    if (isOpen) {
      fetchOperationsMasters();
      fetchRoles();
    }
  }, [isOpen]);

  /**
   * Fetches operation master data and updates the state.
   *
   * This function retrieves a list of modules, their associated actions,
   * and action tags from the server, then updates the `operationMasters` state.
   *
   * @async
   * @function fetchOperationsMasters
   * @returns {Promise<void>} - A promise that resolves when the data is successfully fetched and set in state.
   *
   * @effect
   * - Sends a GET request to `ApiEndpoints.FETCH_OPERATIONS_MASTERS`.
   * - Maps the response data to extract `modules`, `ModuleActions`, and `ModuleActionTags`.
   * - Updates the `operationMasters` state with the processed data.
   * - Displays an error toast if the request fails.
   *
   */
  const fetchOperationsMasters = async (): Promise<void> => {
    try {
      const response = await Axios.get(ApiEndpoints.FETCH_OPERATIONS_MASTERS);
      const modules = response.data.data.module.map((module: Modules) => ({
        id: module.id,
        name: module.name,
        ModuleActions: module.ModuleActions.map((action: ModuleAction) => ({
          id: action.id,
          name: action.name,
          ModuleActionTags: action.ModuleActionTags.map(
            (tag: ModuleActionTag) => ({
              id: tag.id,
              tag: tag.tag,
            }),
          ),
        })),
      }));
      setOperationMasters(modules);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches role data from the server and updates the state.
   *
   * This function sends a POST request to retrieve role data and updates the `roles` state.
   *
   * @async
   * @function fetchRoles
   * @returns {Promise<void>} - A promise that resolves when the roles are successfully fetched and set in state.
   *
   * @effect
   * - Sends a POST request to `ApiEndpoints.FETCH_ROLES`.
   * - Updates the `roles` state with the retrieved data.
   * - Displays an error toast if the request fails.
   *
   */
  const fetchRoles = async (): Promise<void> => {
    try {
      const moduleResponse = await Axios.post(ApiEndpoints.FETCH_ROLES, {});
      setRoles(moduleResponse.data.data);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_MODULES, 'error');
    }
  };

  /**
   * Handles changes to the selected module.
   *
   * This function updates the selected module state based on user input,
   * resets the selected action, and updates form validation errors.
   *
   * @function handleModuleChange
   * @param {React.ChangeEvent<HTMLSelectElement>} event - The change event from the module dropdown.
   * @returns {void} - Does not return a value.
   *
   * @effect
   * - Finds and sets the selected module from `operationMasters`.
   * - Resets the `selectedAction` state to `null`.
   * - Updates `setFormErrors` to clear or display an error message for the module field.
   *
   */
  const handleModuleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const module = operationMasters.find(
      (m) => m.id === Number(event.target.value),
    );
    setSelectedModule(module ?? null);
    setSelectedAction(null);

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      module: module ? '' : errorMessage.FIELD_REQUIRED,
      action: '',
    }));
  };

  /**
   * Handles changes to the selected action within a module.
   *
   * This function updates the selected action state based on user input
   * and updates form validation errors accordingly.
   *
   * @function handleActionChange
   * @param {React.ChangeEvent<HTMLSelectElement>} event - The change event from the action dropdown.
   * @returns {void} - Does not return a value.
   *
   * @effect
   * - If a `selectedModule` exists, finds and sets the selected action.
   * - If no matching action is found, sets `selectedAction` to `null`.
   * - Updates `setFormErrors` to clear or display an error message for the action field.
   *
   */
  const handleActionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (selectedModule) {
      const action = selectedModule.ModuleActions.find(
        (a) => a.id === Number(event.target.value),
      );
      setSelectedAction(action || null);
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        action: action ? '' : errorMessage.FIELD_REQUIRED,
      }));
    }
  };

  /**
   * Handles changes to role selection in a checkbox input.
   *
   * This function returns an event handler that updates the `rolePermissions` state
   * when a role checkbox is checked or unchecked.
   *
   * @function handleRolesChange
   * @param {number} roleId - The ID of the role being selected or deselected.
   * @returns {(event: React.ChangeEvent<HTMLInputElement>) => void} - An event handler function for checkbox changes.
   *
   * @effect
   * - If the checkbox is checked, adds the `roleId` to `rolePermissions`.
   * - If unchecked, removes the `roleId` from `rolePermissions`.
   *
   */
  const handleRolesChange = (roleId: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (event.target.checked) {
        setRolePermissions([...rolePermissions, roleId]);
      } else {
        setRolePermissions(rolePermissions.filter((id) => id !== roleId));
      }
    };
  };

  /**
   * Retrieves and cleans the message content from the rich text editor.
   *
   * Accesses the `innerHTML` of the editor DOM element via `editorRef`,
   * and processes it using `contentConversion` to strip or sanitize HTML content.
   * Returns an empty string if the editor reference is not available.
   *
   * @returns {string} The cleaned plain-text or sanitized message string.
   */
  const getCleanMessage = (): string => {
    if (!editorRef.current) return '';
    return contentConversion(editorRef.current.innerHTML);
  };

  /**
   * Validates the operation form fields before submission.
   *
   * Checks the following fields for validity:
   * - `message`: Must not be empty (cleaned via `getCleanMessage()`).
   * - `selectedModule`: Must be selected.
   * - `selectedAction`: Must be selected.
   * - `rolePermissions`: Must contain at least one role.
   *
   * If any field is invalid, corresponding error messages are set in the `formErrors` state.
   *
   * @returns {boolean} `true` if the form is valid; `false` otherwise.
   */
  const validateForm = (): boolean => {
    const errors: {
      name?: string;
      message?: string;
      module?: string;
      action?: string;
      role?: string;
    } = {};
    const message = getCleanMessage(); //retrieve

    if (!message.trim()) {
      errors.message = errorMessage.FIELD_REQUIRED;
    }
    if (!selectedModule) {
      errors.module = errorMessage.FIELD_REQUIRED;
    }
    if (!selectedAction) {
      errors.action = errorMessage.FIELD_REQUIRED;
    }
    if (!rolePermissions || rolePermissions.length === 0) {
      errors.role = errorMessage.FIELD_REQUIRED;
    }
    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  /**
   * Handles the form submission for creating an operation.
   *
   * This function validates the form, constructs the payload, sends a POST request
   * to create a new operation, and provides feedback to the user.
   *
   * @async
   * @function handleSubmit
   * @param {React.FormEvent} event - The form submission event.
   * @returns {Promise<void>} - A promise that resolves when the request is completed.
   *
   * @effect
   * - Prevents the default form submission behavior.
   * - Calls `validateForm()` and stops execution if validation fails.
   * - Constructs the payload using form data, selected module, action, and role permissions.
   * - Sends a POST request to `ApiEndpoints.CREATE_OPERATION`.
   * - If the request succeeds (`response.status === 200`):
   *   - Calls `onClose()` to close the form.
   *   - Displays a success toast message.
   * - If the request fails, displays an error toast message.
   *
   */
  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!validateForm()) return;

    const message = getCleanMessage();
    try {
      const payload = {
        moduleId: selectedModule?.id,
        moduleActionId: selectedAction?.id,
        message: message,
        operationRoleIds: rolePermissions,
      };
      const response = await Axios.post(ApiEndpoints.CREATE_OPERATION, payload);

      if (response.status === 200) {
        onClose();
        showToastMessage(responseTexts.OPERATION_CREATE_SUCCESS, 'success');
      }
    } catch (error) {
      showToastMessage(errorMessage.ERROR_CREATING_OPERATION, 'error');
    }
  };

  /**
   * Inserts a tag into the editor content and triggers a refresh.
   *
   * If the `editorRef` is available, it calls the `insertTag` utility to insert
   * the given tag into the editor's current position. After insertion, it toggles
   * the `refresh` state to force the component or a dependent effect to update.
   *
   * @param {string} tag - The tag to insert into the editor content.
   */
  const handleTagInsert = (tag: string) => {
    if (editorRef.current) {
      insertTag(editorRef.current, tag, () => setRefresh((prev) => !prev));
      setRefresh((prev) => !prev);
    }
  };

  /**
   * Memoized list of available tags based on the selected action and current editor content.
   *
   * This hook checks if a `selectedAction` is present and the `editorRef` is available.
   * If both conditions are met, it uses `getAvailableTagsFromList` to extract a list
   * of insertable tags, based on the current editor content and the tags defined in
   * `selectedAction.ModuleActionTags`.
   *
   * Dependencies:
   * - `selectedAction`: Triggers recalculation when the selected action changes.
   * - `refresh`: Forces recomputation when the editor content is updated externally (e.g. after inserting a tag).
   *
   * @returns {string[]} Array of available tags to be used in the editor.
   */
  const availableTags = useMemo(() => {
    if (!selectedAction || !editorRef.current) return [];

    return getAvailableTagsFromList(
      editorRef.current,
      selectedAction.ModuleActionTags,
    );
  }, [selectedAction, refresh]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white z-10 border-b px-6 py-3">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold">Add Operations</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-600 hover:text-gray-900"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="overflow-y-auto px-6 py-2 flex-1">
          <form onSubmit={handleSubmit}>
            {/* Module */}
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Modules *
                  <select
                    name="module"
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-primary"
                    onChange={handleModuleChange}
                  >
                    <option value="">Select Module</option>
                    {operationMasters.map((module) => (
                      <option key={module.id} value={module.id}>
                        {module.name}
                      </option>
                    ))}
                  </select>
                </label>
                {formErrors.module && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.module}
                  </p>
                )}
              </div>

              {/* Action */}
              <label className="block text-sm font-medium text-gray-700">
                Actions *
                <select
                  name="action"
                  className="mt-1 block w-full border border-gray-300 hover-bg-[#EFF1F5] rounded-md p-2 text-sm focus:outline-primary"
                  onChange={handleActionChange}
                  disabled={!selectedModule}
                >
                  <option value="">Select Action</option>
                  {selectedModule &&
                    //Only showing actions which are not done in the module
                    getFilteredActions(selectedModule, modules ?? []).map(
                      (action) => (
                        <option key={action.id} value={action.id}>
                          {action.name}
                        </option>
                      ),
                    )}
                </select>
                {formErrors.action && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.action}
                  </p>
                )}
              </label>
            </div>

            {/* Message */}
            <span className="mt-4 block text-sm font-medium text-gray-700">
              Message*
            </span>
            <div className="mt-1 border border-gray-300 rounded-lg p-4">
              <div
                ref={editorRef}
                className="w-full min-h-[80px] p-2 rounded border-none focus:outline-none"
                contentEditable
                onKeyDown={(e) => {
                  // Restricting [] to avoid getting conflicts with [tag]
                  if (e.key === '[' || e.key === ']') {
                    e.preventDefault();
                  }
                }}
              ></div>
              {selectedAction && <hr className="my-4 border-gray-300" />}
              {selectedAction && (
                <div className="flex flex-wrap gap-2 px-2 mb-3 ">
                  {availableTags.map((tag) => {
                    const colors = getColors(tag.tag);
                    return (
                      <button
                        type="button"
                        key={tag.id}
                        onClick={() => handleTagInsert(tag.tag)}
                        className="px-3 py-1 rounded-full text-sm transition"
                        style={{
                          backgroundColor: colors.backgroundColor,
                          border: `1px solid ${colors.borderColor}`,
                          color: colors.textColor,
                        }}
                      >
                        {tag.tag}
                      </button>
                    );
                  })}
                </div>
              )}
              {formErrors.message && (
                <p className="text-red-500 text-sm mt-1">
                  {formErrors.message}
                </p>
              )}
            </div>

            {/* Notification */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                Notification sent to:*{' '}
                <div className="grid grid-cols-2 gap-2 mt-1 border p-2 pt-4 border-gray-300 rounded-md">
                  {roles.map((role: Role) => (
                    <label
                      key={role.id}
                      className="flex items-center justify-between mr-4 mb-4"
                    >
                      <span className="mr-2 text-sm font-semibold">
                        {role.name}
                      </span>
                      <input
                        type="checkbox"
                        className="w-auto h-auto rounded accent-primary"
                        onChange={handleRolesChange(Number(role.id))}
                      />
                    </label>
                  ))}
                </div>
              </label>
              {formErrors.role && (
                <p className="text-red-500 text-sm mt-1">{formErrors.role}</p>
              )}
            </div>

            {/* Modal Footer Buttons */}
            <div className="mt-6 flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="text-gray-600 px-4 py-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 h-10 rounded-md text-white bg-primary"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
